import type {
  EligibilityCheckRequest,
  EligibilityCurrentResponse,
  EligibilityManualRequest,
  EligibilityWriteErrorResponse,
} from '@features/patient-coverages/types';

import type { ManualEligibilityDraft } from '../hooks/useManualEligibilityDraft';

import { buildFinancialsPatch } from './verificationFinancials';

export function eligibilityPrecondition(
  current: EligibilityCurrentResponse,
): Omit<EligibilityCheckRequest, 'idempotency_key'> {
  return current.state === 'record'
    ? { expected_revision: current.revision }
    : { baseline_token: current.baseline_token };
}

export function manualEligibilityCommand(
  draft: ManualEligibilityDraft,
  current: EligibilityCurrentResponse,
  key: string,
): EligibilityManualRequest {
  if (!draft.result) throw new Error('Choose a coverage result.');
  return {
    ...eligibilityPrecondition(current),
    idempotency_key: key,
    coverage_result: draft.result,
    contacted_at: new Date(draft.contactedAt).toISOString(),
    method: draft.method,
    benefits: buildFinancialsPatch({}, draft.financials, draft.assertedFields) ?? {},
    representative_name: draft.method === 'phone' ? draft.representativeName.trim() || null : null,
    reference_number: draft.referenceNumber.trim() || null,
    notes: draft.notes.trim() || null,
  };
}

/** Read the documented API error envelope; unrelated errors stay generic. */
export function eligibilityWriteError(error: unknown): EligibilityWriteErrorResponse | null {
  if (!error || typeof error !== 'object' || !('data' in error)) return null;
  const envelope = error.data;
  // auth0BaseQuery puts the server body inside CustomErrorResponse.data.
  // Keep direct FetchBaseQueryError bodies supported at the same boundary.
  const data =
    envelope &&
    typeof envelope === 'object' &&
    'status' in envelope &&
    typeof envelope.status === 'number' &&
    'message' in envelope &&
    typeof envelope.message === 'string' &&
    'data' in envelope
      ? envelope.data
      : envelope;
  if (!data || typeof data !== 'object' || !('error' in data)) return null;
  if (
    data.error === 'eligibility_command_in_progress' ||
    data.error === 'eligibility_idempotency_conflict' ||
    data.error === 'eligibility_check_failed' ||
    data.error === 'eligibility_check_outcome_uncertain'
  )
    return { error: data.error };
  if (data.error === 'eligibility_stale_state' && 'current' in data) {
    const current = data.current;
    if (
      current &&
      typeof current === 'object' &&
      'state' in current &&
      ['record', 'empty', 'legacy'].includes(String(current.state)) &&
      'patient_id' in current &&
      typeof current.patient_id === 'string' &&
      'patient_insurance_coverage_id' in current &&
      typeof current.patient_insurance_coverage_id === 'string' &&
      'organization_id' in current &&
      typeof current.organization_id === 'string' &&
      (current.state === 'record'
        ? 'revision' in current && typeof current.revision === 'number'
        : 'baseline_token' in current && typeof current.baseline_token === 'string')
    ) {
      return data as EligibilityWriteErrorResponse;
    }
  }
  return null;
}
